import type { ScannerType } from '@/stores/rfidSessionStore'

export type KnownScannerDevice = {
  /** Stable key (Bluetooth id, HID id, or normalized name). */
  id: string
  /** Exact machine / host name, e.g. RFD8500192505230012 */
  name: string
  type: ScannerType
  source: 'bluetooth' | 'manual' | 'hid'
  lastSeenAt: string
  /** Bluetooth MAC when known (native bridge). */
  address?: string
}

const STORAGE_KEY = 'rfid-known-scanners-v1'

const RFD8500_NAME = /RFD\s?8500|RFD8500/i
const RFD40_NAME = /RFD\s?40|RFD\s?90|RFD40|RFD90/i

function readAll(): KnownScannerDevice[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as KnownScannerDevice[]
    if (!Array.isArray(parsed)) return []
    return parsed.filter((d) => d?.id && d?.name && (d.type === 'RFD8500' || d.type === 'RFD40'))
  } catch {
    return []
  }
}

function writeAll(devices: KnownScannerDevice[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(devices))
}

export function isRfidScannerName(name: string, type?: ScannerType): boolean {
  const n = name.trim()
  if (!n) return false
  if (type === 'RFD40') return RFD40_NAME.test(n)
  if (type === 'RFD8500') return RFD8500_NAME.test(n) || (/^RFD/i.test(n) && !RFD40_NAME.test(n))
  return RFD8500_NAME.test(n) || RFD40_NAME.test(n) || /^RFD\d/i.test(n)
}

export function listKnownScanners(type?: ScannerType): KnownScannerDevice[] {
  const all = readAll().sort((a, b) => b.lastSeenAt.localeCompare(a.lastSeenAt))
  return type ? all.filter((d) => d.type === type) : all
}

export function upsertKnownScanner(
  device: Omit<KnownScannerDevice, 'lastSeenAt'> & { lastSeenAt?: string }
): KnownScannerDevice {
  const next: KnownScannerDevice = {
    ...device,
    name: device.name.trim(),
    lastSeenAt: device.lastSeenAt ?? new Date().toISOString(),
  }
  const all = readAll().filter(
    (d) => d.id !== next.id && d.name.toUpperCase() !== next.name.toUpperCase()
  )
  all.unshift(next)
  writeAll(all.slice(0, 40))
  return next
}

export function removeKnownScanner(id: string) {
  writeAll(readAll().filter((d) => d.id !== id))
}

export function inferScannerType(name: string): ScannerType {
  if (RFD40_NAME.test(name)) return 'RFD40'
  return 'RFD8500'
}

export function isWebBluetoothAvailable(): boolean {
  return typeof navigator !== 'undefined' && Boolean(navigator.bluetooth?.requestDevice)
}

export function isWebHidAvailable(): boolean {
  return typeof navigator !== 'undefined' && Boolean(navigator.hid?.getDevices)
}

/** Zebra Technologies USB vendor id */
const ZEBRA_VENDOR_ID = 0x05e0

function namePrefixesFor(type: ScannerType): string[] {
  return type === 'RFD40'
    ? ['RFD40', 'RFD90']
    : ['RFD8500', 'RFD850']
}

/**
 * Refresh permitted Web Bluetooth + Zebra HID devices into the registry.
 * Only keeps names that look like RFD scanner machine codes.
 */
export async function refreshAvailableScanners(
  type: ScannerType
): Promise<KnownScannerDevice[]> {
  if (isWebBluetoothAvailable()) {
    try {
      const btDevices = await navigator.bluetooth!.getDevices()
      for (const d of btDevices) {
        const name = (d.name || '').trim()
        if (!isRfidScannerName(name, type)) continue
        upsertKnownScanner({
          id: `bt:${d.id}`,
          name,
          type: inferScannerType(name) === type ? type : inferScannerType(name),
          source: 'bluetooth',
        })
      }
    } catch {
      /* permission / unsupported */
    }
  }

  if (isWebHidAvailable()) {
    try {
      const hidDevices = await navigator.hid!.getDevices()
      for (const d of hidDevices) {
        if (d.vendorId !== ZEBRA_VENDOR_ID) continue
        const name = (d.productName || '').trim()
        if (!isRfidScannerName(name, type)) continue
        upsertKnownScanner({
          id: `hid:${d.vendorId}:${d.productId}:${name}`,
          name,
          type: inferScannerType(name) === type ? type : inferScannerType(name),
          source: 'hid',
        })
      }
    } catch {
      /* permission / unsupported */
    }
  }

  return listKnownScanners(type)
}

/**
 * One-time browser permission dialog filtered to RFD* name prefixes only
 * (never acceptAllDevices). Device is saved into our list — connect happens in-app.
 */
export async function authorizeNearbyRfidScanner(
  type: ScannerType
): Promise<KnownScannerDevice> {
  if (!isWebBluetoothAvailable()) {
    throw new Error(
      'This browser cannot discover Bluetooth devices. Add the machine code from OS Bluetooth settings.'
    )
  }

  const prefixes = namePrefixesFor(type)
  const device = await navigator.bluetooth!.requestDevice({
    filters: prefixes.map((namePrefix) => ({ namePrefix })),
    optionalServices: ['battery_service', 'device_information'],
  })

  const name = (device.name || '').trim()
  if (!name) {
    throw new Error('Selected device has no machine code / name')
  }
  if (!isRfidScannerName(name, type)) {
    throw new Error(`“${name}” is not an ${type} scanner. Pick an RFD machine code.`)
  }

  return upsertKnownScanner({
    id: `bt:${device.id}`,
    name,
    type: inferScannerType(name) === type ? type : inferScannerType(name),
    source: 'bluetooth',
  })
}

/** @deprecated use authorizeNearbyRfidScanner */
export async function pickBluetoothScanner(type: ScannerType) {
  return authorizeNearbyRfidScanner(type)
}

/**
 * Request permission to a Zebra HID device and register its product name.
 */
export async function pickHidScanner(type: ScannerType): Promise<KnownScannerDevice> {
  if (!isWebHidAvailable()) {
    throw new Error('WebHID is not available in this browser. Add the machine code manually.')
  }

  const devices = await navigator.hid!.requestDevice({
    filters: [{ vendorId: ZEBRA_VENDOR_ID }],
  })
  const d = devices[0]
  if (!d) throw new Error('No HID device selected')
  const name = (d.productName || '').trim()
  if (!name) throw new Error('Selected HID device has no product name')
  if (!isRfidScannerName(name, type)) {
    throw new Error(`“${name}” does not look like an ${type} scanner`)
  }

  return upsertKnownScanner({
    id: `hid:${d.vendorId}:${d.productId}:${name}`,
    name,
    type: inferScannerType(name) === type ? type : inferScannerType(name),
    source: 'hid',
  })
}

export function addManualScanner(type: ScannerType, machineCode: string): KnownScannerDevice {
  const name = machineCode.trim()
  if (!name) throw new Error('Enter the scanner machine code')
  if (!isRfidScannerName(name, type)) {
    throw new Error(
      type === 'RFD8500'
        ? 'Machine code should look like RFD8500…'
        : 'Machine code should look like RFD40… / RFD90…'
    )
  }
  return upsertKnownScanner({
    id: `manual:${type}:${name.toUpperCase()}`,
    name,
    type,
    source: 'manual',
  })
}
